import React, { useState } from "react";
import { NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  Sparkles,
  Shield,
  ScrollText,
  LogOut,
  Building2,
  CreditCard,
  Users,
  Briefcase,
  ToggleLeft,
  Megaphone,
  Activity,
  Map as MapIcon,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { PlatformAlertsBell } from "./PlatformAlertsBell";

const NAV = [
  { to: "/super-admin/tenants", icon: Building2, label: "Empresas", match: (p) => p.startsWith("/super-admin/tenants") && !p.includes("/menus") },
  { to: "/super-admin/plans", icon: CreditCard, label: "Planos", match: (p) => p.startsWith("/super-admin/plans") },
  { to: "/super-admin/collaborators", icon: Users, label: "Colaboradores", match: (p) => p.startsWith("/super-admin/collaborators") },
  { to: "/super-admin/clients", icon: Briefcase, label: "Clientes", match: (p) => p.startsWith("/super-admin/clients") },
  { to: "/super-admin/talent-map", icon: MapIcon, label: "Mapa Helps", match: (p) => p.startsWith("/super-admin/talent-map") },
  { to: "/super-admin/menus", icon: ToggleLeft, label: "Menus", match: (p) => p.startsWith("/super-admin/menus") },
  { to: "/super-admin/release-notes", icon: Megaphone, label: "Avisos", match: (p) => p.startsWith("/super-admin/release-notes") },
  { to: "/super-admin/audit-log", icon: ScrollText, label: "Audit", match: (p) => p.startsWith("/super-admin/audit-log") },
  { to: "/super-admin/health-audit", icon: Activity, label: "Uso API", match: (p) => p.startsWith("/super-admin/health-audit") },
];

export function SuperAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Convenção do AuthContext: null = carregando, false = visitante, obj = logado.
  // NÃO redirecionar durante o carregamento (senão o super admin "cai" a cada reload).
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">
        Carregando…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/platform/login" replace />;
  }
  if (!user.is_super_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <Shield size={36} className="mx-auto text-red-600 mb-3" />
          <h2 className="text-lg font-semibold text-red-800">Acesso restrito</h2>
          <p className="text-sm text-red-700 mt-1">
            Esta área é exclusiva do super-administrador da plataforma.
          </p>
          <button
            onClick={() => nav("/platform/login", { replace: true })}
            className="mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          >
            Ir para o portal da plataforma
          </button>
        </div>
      </div>
    );
  }

  const currentLabel = NAV.find((n) => n.match(loc.pathname))?.label || "Platform";

  async function doLogout() {
    await logout();
    nav("/platform/login", { replace: true });
  }

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 pb-20 lg:pb-0"
      data-testid="super-admin-layout"
    >
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur bg-gradient-to-r from-violet-950/95 via-purple-900/95 to-indigo-900/95 border-b border-violet-800/40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"
              data-testid="super-admin-burger"
              aria-label="Abrir menu"
            >
              <Menu size={18} />
            </button>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30 flex-shrink-0">
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-violet-300/80 font-semibold">
                Super Admin
              </div>
              <div className="text-sm sm:text-lg font-bold text-white truncate">
                {currentLabel}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <PlatformAlertsBell />
            <button
              onClick={doLogout}
              data-testid="super-admin-logout"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-sm font-semibold text-red-200 border border-red-500/30 transition"
            >
              <LogOut size={14} /> Sair
            </button>
            <button
              onClick={doLogout}
              className="sm:hidden p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200"
              aria-label="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Desktop tabs */}
        <nav className="hidden lg:flex max-w-7xl mx-auto px-4 sm:px-6 -mb-px gap-1 overflow-x-auto">
          {NAV.map((n) => (
            <Tab key={n.to} to={n.to} icon={n.icon} active={n.match(loc.pathname)}>
              {n.label}
            </Tab>
          ))}
        </nav>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">{children}</main>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="super-admin-drawer"
        >
          <div
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-gradient-to-b from-violet-950 via-purple-950 to-slate-950 border-r border-violet-800/40 p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-widest text-violet-300 font-bold">
                Menu Super Admin
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-1">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = n.match(loc.pathname);
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setDrawerOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold transition ${
                      active
                        ? "bg-fuchsia-500/20 text-white border border-fuchsia-500/30"
                        : "text-violet-200 hover:bg-white/5"
                    }`}
                  >
                    <Icon size={17} />
                    {n.label}
                  </NavLink>
                );
              })}
            </div>
            <button
              onClick={doLogout}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-sm font-bold text-red-200 border border-red-500/30"
            >
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      )}

      {/* Mobile bottom tab bar — 4 most used items */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-slate-900/95 backdrop-blur border-t border-violet-800/40 grid grid-cols-4 px-1 py-1">
        {NAV.slice(0, 4).map((n) => {
          const Icon = n.icon;
          const active = n.match(loc.pathname);
          return (
            <NavLink
              key={n.to}
              to={n.to}
              className={`flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-lg text-[10px] font-semibold transition ${
                active ? "bg-fuchsia-500/20 text-white" : "text-violet-300/80"
              }`}
              data-testid={`mobile-tab-${n.label.toLowerCase()}`}
            >
              <Icon size={18} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>

      <footer className="py-4 text-center text-[10px] text-slate-600">
        Platform Admin · v2026
      </footer>
    </div>
  );
}

function Tab({ to, icon: Icon, children, active }) {
  return (
    <NavLink
      to={to}
      className={() =>
        `inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition ${
          active
            ? "text-white border-fuchsia-400"
            : "text-violet-200/80 border-transparent hover:text-white hover:border-violet-500"
        }`
      }
    >
      <Icon size={15} />
      {children}
    </NavLink>
  );
}
